import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import api from '../services/api.ts';
import { formatCurrency, parseCurrency, applyCurrencyMask } from '../utils/currency.ts';
import {
  ArrowLeftIcon,
  ArrowRightIcon,
  BuildingOfficeIcon,
  TruckIcon,
  CubeIcon,
  PlusIcon,
  XMarkIcon,
  CurrencyDollarIcon,
  CalendarIcon,
  DocumentTextIcon,
} from '@heroicons/react/24/outline';

// Types
interface Asset {
  id: string;
  description: string;
  type: 'real_estate' | 'vehicle' | 'other';
  currentValue: number;
  status: string;
}

interface ExchangeAsset {
  description: string;
  type: 'real_estate' | 'vehicle' | 'other';
  acquisitionValue: number;
  currentValue: number;
  acquisitionDate: string;
  details: any;
}

interface ExchangeFormData {
  exchangeDate: string;
  totalValue: number;
  notes: string;
  newAssets: ExchangeAsset[];
}

const AssetExchange: React.FC = () => {
  console.log('🔄 AssetExchange component renderizado');

  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [formData, setFormData] = useState<ExchangeFormData>({
    exchangeDate: new Date().toISOString().split('T')[0],
    totalValue: 0,
    notes: '',
    newAssets: [],
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  // Estados para controlar a formatação dos inputs
  const [formattedValues, setFormattedValues] = useState<Record<string, string>>({});

  // Query para buscar dados do ativo original
  const { data: originalAsset, isLoading } = useQuery<Asset>(
    ['asset', id],
    async () => {
      const response = await api.get(`/assets/${id}`);
      return response.data;
    },
    {}
  );

  // Efeito para calcular automaticamente o valor total da operação
  useEffect(() => {
    const totalNewAssetsValue = formData.newAssets.reduce(
      (total, asset) => total + asset.currentValue,
      0
    );

    setFormData(prev => ({
      ...prev,
      totalValue: totalNewAssetsValue,
    }));
  }, [formData.newAssets]);

  // Mutation para registrar permuta
  const exchangeMutation = useMutation(
    async (data: ExchangeFormData) => {
      console.log('🔄 Iniciando permuta com dados:', data);
      console.log('📡 Fazendo requisição para:', `/assets/${id}/exchange`);

      const response = await api.post(`/assets/${id}/exchange`, data);
      console.log('✅ Resposta da permuta:', response.data);
      return response.data;
    },
    {
      onSuccess: data => {
        console.log('🎉 Permuta realizada com sucesso:', data);
        queryClient.invalidateQueries(['asset', id]);
        queryClient.invalidateQueries(['assets']);
        navigate(`/assets/${id}`);
      },
      onError: (error: any) => {
        console.error('❌ Erro ao registrar permuta:', error);
        console.error('📋 Detalhes do erro:', error.response?.data);
        console.error('🔢 Status do erro:', error.response?.status);
      },
    }
  );

  const addNewAsset = () => {
    const newAsset: ExchangeAsset = {
      description: '',
      type: 'real_estate',
      acquisitionValue: 0,
      currentValue: 0,
      acquisitionDate: formData.exchangeDate,
      details: {},
    };

    setFormData(prev => ({
      ...prev,
      newAssets: [...prev.newAssets, newAsset],
    }));
  };

  const removeNewAsset = (index: number) => {
    setFormData(prev => ({
      ...prev,
      newAssets: prev.newAssets.filter((_, i) => i !== index),
    }));
  };

  const updateNewAsset = (index: number, field: string, value: any) => {
    setFormData(prev => ({
      ...prev,
      newAssets: prev.newAssets.map((asset, i) =>
        i === index ? { ...asset, [field]: value } : asset
      ),
    }));
  };

  const updateNewAssetDetails = (index: number, field: string, value: any) => {
    setFormData(prev => ({
      ...prev,
      newAssets: prev.newAssets.map((asset, i) =>
        i === index
          ? {
              ...asset,
              details: { ...asset.details, [field]: value },
            }
          : asset
      ),
    }));
  };

  // Função para lidar com inputs de valores monetários formatados
  const handleCurrencyInput = (
    index: number,
    field: 'acquisitionValue' | 'currentValue',
    value: string
  ) => {
    const formattedValue = applyCurrencyMask(value);
    const numericValue = parseCurrency(formattedValue);

    // Atualiza o valor formatado para exibição
    setFormattedValues(prev => ({
      ...prev,
      [`${index}_${field}`]: formattedValue,
    }));

    // Atualiza o valor numérico no estado do formulário
    updateNewAsset(index, field, numericValue);
  };

  // Função para obter o valor formatado para exibição
  const getFormattedValue = (
    index: number,
    field: 'acquisitionValue' | 'currentValue',
    numericValue: number
  ): string => {
    const key = `${index}_${field}`;
    return formattedValues[key] !== undefined
      ? formattedValues[key]
      : numericValue > 0
        ? applyCurrencyMask(numericValue.toString())
        : '';
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.exchangeDate) {
      newErrors.exchangeDate = 'Data da permuta é obrigatória';
    }

    if (formData.newAssets.length === 0) {
      newErrors.newAssets = 'Pelo menos um novo ativo deve ser adicionado';
    }

    formData.newAssets.forEach((asset, index) => {
      if (!asset.description.trim()) {
        newErrors[`newAsset_${index}_description`] = 'Descrição é obrigatória';
      }
      if (asset.acquisitionValue <= 0) {
        newErrors[`newAsset_${index}_acquisitionValue`] =
          'Valor de aquisição deve ser maior que zero';
      }
      if (asset.currentValue <= 0) {
        newErrors[`newAsset_${index}_currentValue`] = 'Valor atual deve ser maior que zero';
      }
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    console.log('📝 Formulário submetido');
    console.log('📊 Dados do formulário:', formData);
    console.log('🔍 Validando formulário...');

    if (validateForm()) {
      console.log('✅ Validação passou, executando mutation');
      exchangeMutation.mutate(formData);
    } else {
      console.log('❌ Validação falhou, erros:', errors);
    }
  };

  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'real_estate':
        return <BuildingOfficeIcon className="h-5 w-5" />;
      case 'vehicle':
        return <TruckIcon className="h-5 w-5" />;
      default:
        return <CubeIcon className="h-5 w-5" />;
    }
  };

  const getTotalNewAssetsValue = () => {
    return formData.newAssets.reduce((total, asset) => total + asset.currentValue, 0);
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-purple-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 py-8">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <button
            onClick={() => navigate(`/assets/${id}`)}
            className="flex items-center text-gray-600 hover:text-gray-900 mb-4 transition-colors"
          >
            <ArrowLeftIcon className="h-5 w-5 mr-2" />
            Voltar para Detalhes do Ativo
          </button>

          <div className="flex items-center">
            <div className="p-3 rounded-full bg-purple-100 text-purple-600 mr-4">
              <ArrowRightIcon className="h-6 w-6" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Registrar Permuta</h1>
              <p className="text-gray-600 mt-1">Permutando: {originalAsset?.description}</p>
            </div>
          </div>
        </div>

        <div
          className="space-y-8"
          onClick={() => console.log('🖱️ Clique no formulário detectado')}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              console.log('⌨️ Tecla pressionada no formulário:', e.key);
            }
          }}
          role="button"
          tabIndex={0}
          aria-label="Área de formulário interativa"
        >
          <form onSubmit={handleSubmit} className="space-y-8">
          {/* Ativo Original */}
          <div className="bg-white rounded-xl shadow-lg p-6">
            <h3 className="text-xl font-semibold text-gray-900 mb-6 flex items-center">
              <div className="p-2 rounded-full bg-red-100 text-red-600 mr-3">
                {originalAsset && getTypeIcon(originalAsset.type)}
              </div>
              Ativo a ser Permutado
            </h3>

            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <div className="flex items-center justify-between">
                <div>
                  <h4 className="font-medium text-gray-900">{originalAsset?.description}</h4>
                  <p className="text-sm text-gray-600 mt-1">
                    Valor atual: {formatCurrency(originalAsset?.currentValue || 0)}
                  </p>
                </div>
                <span className="bg-red-100 text-red-800 px-3 py-1 rounded-full text-sm font-medium">
                  Será Permutado
                </span>
              </div>
            </div>
          </div>

          {/* Informações da Permuta */}
          <div className="bg-white rounded-xl shadow-lg p-6">
            <h3 className="text-xl font-semibold text-gray-900 mb-6">Informações da Permuta</h3>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Data da Permuta *
                </label>
                <div className="relative">
                  <CalendarIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                  <input
                    type="date"
                    value={formData.exchangeDate}
                    onChange={e => setFormData(prev => ({ ...prev, exchangeDate: e.target.value }))}
                    className={`w-full pl-10 pr-4 py-3 border rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent ${
                      errors.exchangeDate ? 'border-red-300' : 'border-gray-300'
                    }`}
                  />
                </div>
                {errors.exchangeDate && (
                  <p className="mt-1 text-sm text-red-600">{errors.exchangeDate}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Valor Total da Operação (Calculado Automaticamente)
                </label>
                <div className="relative">
                  <CurrencyDollarIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                  <input
                    type="text"
                    value={formatCurrency(formData.totalValue)}
                    readOnly
                    className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg bg-gray-50 text-gray-700 cursor-not-allowed"
                    placeholder="R$ 0,00"
                  />
                </div>
                <p className="mt-1 text-xs text-gray-500">
                  Soma dos valores dos novos ativos recebidos na permuta
                </p>
              </div>

              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">Observações</label>
                <div className="relative">
                  <DocumentTextIcon className="absolute left-3 top-3 h-5 w-5 text-gray-400" />
                  <textarea
                    value={formData.notes}
                    onChange={e => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                    rows={3}
                    className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    placeholder="Detalhes adicionais sobre a permuta..."
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Novos Ativos */}
          <div className="bg-white rounded-xl shadow-lg p-6">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-semibold text-gray-900 flex items-center">
                <div className="p-2 rounded-full bg-green-100 text-green-600 mr-3">
                  <PlusIcon className="h-5 w-5" />
                </div>
                Novos Ativos Recebidos
              </h3>

              <button
                type="button"
                onClick={addNewAsset}
                className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors flex items-center"
              >
                <PlusIcon className="h-4 w-4 mr-2" />
                Adicionar Ativo
              </button>
            </div>

            {errors.newAssets && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-sm text-red-600">{errors.newAssets}</p>
              </div>
            )}

            {formData.newAssets.length === 0 ? (
              <div className="text-center py-8 border-2 border-dashed border-gray-300 rounded-lg">
                <CubeIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-600 mb-4">Nenhum novo ativo adicionado ainda</p>
                <button
                  type="button"
                  onClick={addNewAsset}
                  className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors"
                >
                  Adicionar Primeiro Ativo
                </button>
              </div>
            ) : (
              <div className="space-y-6">
                {formData.newAssets.map((asset, index) => (
                  <div key={index} className="border border-gray-200 rounded-lg p-6 relative">
                    <button
                      type="button"
                      onClick={() => removeNewAsset(index)}
                      className="absolute top-4 right-4 text-gray-400 hover:text-red-600 transition-colors"
                    >
                      <XMarkIcon className="h-5 w-5" />
                    </button>

                    <h4 className="font-medium text-gray-900 mb-4">Ativo {index + 1}</h4>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="md:col-span-2">
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Descrição *
                        </label>
                        <input
                          type="text"
                          value={asset.description}
                          onChange={e => updateNewAsset(index, 'description', e.target.value)}
                          className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent ${
                            errors[`newAsset_${index}_description`]
                              ? 'border-red-300'
                              : 'border-gray-300'
                          }`}
                          placeholder="Ex: Casa no Jardim América - 4 quartos"
                        />
                        {errors[`newAsset_${index}_description`] && (
                          <p className="mt-1 text-sm text-red-600">
                            {errors[`newAsset_${index}_description`]}
                          </p>
                        )}
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Tipo *
                        </label>
                        <select
                          value={asset.type}
                          onChange={e => updateNewAsset(index, 'type', e.target.value)}
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                        >
                          <option value="real_estate">Imóvel</option>
                          <option value="other">Outro</option>
                        </select>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Data de Aquisição
                        </label>
                        <input
                          type="date"
                          value={asset.acquisitionDate}
                          onChange={e => updateNewAsset(index, 'acquisitionDate', e.target.value)}
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Valor de Aquisição *
                        </label>
                        <input
                          type="text"
                          value={getFormattedValue(
                            index,
                            'acquisitionValue',
                            asset.acquisitionValue
                          )}
                          onChange={e =>
                            handleCurrencyInput(index, 'acquisitionValue', e.target.value)
                          }
                          className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent ${
                            errors[`newAsset_${index}_acquisitionValue`]
                              ? 'border-red-300'
                              : 'border-gray-300'
                          }`}
                          placeholder="0,00"
                        />
                        {errors[`newAsset_${index}_acquisitionValue`] && (
                          <p className="mt-1 text-sm text-red-600">
                            {errors[`newAsset_${index}_acquisitionValue`]}
                          </p>
                        )}
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Valor Atual *
                        </label>
                        <input
                          type="text"
                          value={getFormattedValue(index, 'currentValue', asset.currentValue)}
                          onChange={e => handleCurrencyInput(index, 'currentValue', e.target.value)}
                          className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent ${
                            errors[`newAsset_${index}_currentValue`]
                              ? 'border-red-300'
                              : 'border-gray-300'
                          }`}
                          placeholder="0,00"
                        />
                        {errors[`newAsset_${index}_currentValue`] && (
                          <p className="mt-1 text-sm text-red-600">
                            {errors[`newAsset_${index}_currentValue`]}
                          </p>
                        )}
                      </div>

                      {/* Campos específicos por tipo */}
                      {asset.type === 'real_estate' && (
                        <>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              Endereço
                            </label>
                            <input
                              type="text"
                              value={asset.details.address || ''}
                              onChange={e =>
                                updateNewAssetDetails(index, 'address', e.target.value)
                              }
                              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                              placeholder="Ex: Rua das Flores, 123"
                            />
                          </div>

                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              Área (m²)
                            </label>
                            <input
                              type="number"
                              value={asset.details.area || ''}
                              onChange={e =>
                                updateNewAssetDetails(
                                  index,
                                  'area',
                                  parseFloat(e.target.value) || 0
                                )
                              }
                              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                              placeholder="0"
                            />
                          </div>

                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              Quartos
                            </label>
                            <input
                              type="number"
                              value={asset.details.rooms || ''}
                              onChange={e =>
                                updateNewAssetDetails(index, 'rooms', parseInt(e.target.value) || 0)
                              }
                              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                              placeholder="0"
                            />
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                ))}

                {/* Resumo dos Valores */}
                <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                  <div className="flex items-center justify-between">
                    <span className="font-medium text-gray-900">Total dos Novos Ativos:</span>
                    <span className="text-lg font-semibold text-green-600">
                      {formatCurrency(getTotalNewAssetsValue())}
                    </span>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Botões de Ação */}
          <div className="flex justify-end space-x-4">
            <button
              type="button"
              onClick={() => navigate(`/assets/${id}`)}
              className="px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={exchangeMutation.isLoading}
              onClick={() => console.log('🖱️ Botão "Registrar Permuta" clicado')}
              className="px-6 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 transition-colors flex items-center"
            >
              {exchangeMutation.isLoading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Processando...
                </>
              ) : (
                <>
                  <ArrowRightIcon className="h-4 w-4 mr-2" />
                  Registrar Permuta
                </>
              )}
            </button>
          </div>
        </form>
        </div>
      </div>
    </div>
  );
};

export default AssetExchange;
